/**
 * Telegram ID linkage helpers.
 *
 * Когда пользователь приходит на сайт по ссылке из Telegram-бота «Эфир»
 * (@Drag_Expert_bot), URL содержит параметр `?user_id=TELEGRAM_ID`.
 * Мы сохраняем этот ID в localStorage с TTL 14 дней, чтобы при checkout
 * связать заказ с Telegram-аккаунтом покупателя для последующего
 * сопровождения ботом (чек-ины, refill-напоминания, прогресс курса).
 *
 * ВАЖНО: имя URL-параметра именно `user_id` — поддерживаем совместимость
 * с уже разосланными ссылками бота.
 */

const STORAGE_KEY = "bp_tg_id";
const TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 дней
const TG_ID_REGEX = /^\d{5,15}$/;

interface StoredTelegramId {
  id: number;
  exp: number;
}

/**
 * Прочитать `?user_id=` из текущего URL и, если валидный,
 * сохранить в localStorage с TTL 14 дней.
 * Безопасно вызывать на каждой навигации.
 */
export function captureTelegramIdFromUrl(): void {
  if (typeof window === "undefined") return;

  try {
    const tgParam = new URL(window.location.href).searchParams.get("user_id");
    if (!tgParam || !TG_ID_REGEX.test(tgParam)) return;

    const payload: StoredTelegramId = {
      id: parseInt(tgParam, 10),
      exp: Date.now() + TTL_MS,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore — localStorage может быть заблокирован
  }
}

/**
 * Прочитать сохранённый Telegram ID. Возвращает null если:
 * - значение отсутствует
 * - TTL истёк (запись очищается)
 * - повреждённый JSON (запись очищается)
 */
export function getStoredTelegramId(): number | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredTelegramId;
    if (
      typeof parsed?.id !== "number" ||
      typeof parsed?.exp !== "number" ||
      Date.now() > parsed.exp
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.id;
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    return null;
  }
}

/** Очистить сохранённый Telegram ID (например, при logout). */
export function clearStoredTelegramId(): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
